import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo'

type Gender = 'male' | 'female'
type UnitSystem = 'metric' | 'imperial'
type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active'
type Goal = 'lose' | 'maintain' | 'gain'
type DietType = 'balanced' | 'low_carb' | 'high_protein'

interface MacroResults {
  bmr: number
  tdee: number
  calories: number
  protein: number
  carbs: number
  fat: number
}

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val))

const useNumberInput = (initial: number, min: number, max: number) => {
  const [raw, setRaw] = useState(String(initial))
  const value = raw === '' ? min : Number(raw)
  const set = (n: number) => setRaw(String(clamp(n, min, max)))
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setRaw(e.target.value)
  const onBlur = () => setRaw(String(clamp(raw === '' ? min : Number(raw), min, max)))
  const inc = () => set(value + 1)
  const dec = () => set(value - 1)
  return { raw, value, set, onChange, onBlur, inc, dec }
}

const MacroCalculator: React.FC = () => {
  const [gender, setGender] = useState<Gender>('female')
  const age = useNumberInput(35, 13, 99)
  const [units, setUnits] = useState<UnitSystem>('imperial')
  const heightCm = useNumberInput(165, 100, 250)
  const heightFt = useNumberInput(5, 3, 8)
  const heightIn = useNumberInput(5, 0, 11)
  const weight = useNumberInput(150, 50, 700)
  const goalWeight = useNumberInput(140, 50, 700)
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('moderate')
  const [goal, setGoal] = useState<Goal>('lose')
  const [dietType, setDietType] = useState<DietType>('balanced')
  const [results, setResults] = useState<MacroResults | null>(null)

  const activityOptions: { value: ActivityLevel; label: string; desc: string }[] = [
    { value: 'sedentary', label: 'Sedentary', desc: 'Little or no exercise' },
    { value: 'light', label: 'Lightly Active', desc: 'Light exercise 1–3 days/week' },
    { value: 'moderate', label: 'Moderately Active', desc: 'Moderate exercise 3–5 days/week' },
    { value: 'active', label: 'Active', desc: 'Hard exercise 6–7 days/week' },
    { value: 'very_active', label: 'Very Active', desc: 'Intense training or physical job' },
  ]

  const goalOptions: { value: Goal; label: string }[] = [
    { value: 'lose', label: 'Lose Fat' },
    { value: 'maintain', label: 'Maintain' },
    { value: 'gain', label: 'Build Muscle' },
  ]

  const dietOptions: { value: DietType; label: string; split: string }[] = [
    { value: 'balanced', label: 'Balanced', split: '30P / 40C / 30F' },
    { value: 'low_carb', label: 'Low Carb', split: '35P / 25C / 40F' },
    { value: 'high_protein', label: 'High Protein', split: '40P / 35C / 25F' },
  ]

  const activityMultipliers: Record<ActivityLevel, number> = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
  }

  const macroSplits: Record<DietType, { protein: number; carbs: number; fat: number }> = {
    balanced: { protein: 0.30, carbs: 0.40, fat: 0.30 },
    low_carb: { protein: 0.35, carbs: 0.25, fat: 0.40 },
    high_protein: { protein: 0.40, carbs: 0.35, fat: 0.25 },
  }

  const calculate = () => {
    let weightKg: number
    let heightCmFinal: number

    if (units === 'imperial') {
      weightKg = weight.value * 0.453592
      heightCmFinal = (heightFt.value * 12 + heightIn.value) * 2.54
    } else {
      weightKg = weight.value
      heightCmFinal = heightCm.value
    }

    // Mifflin-St Jeor equation
    let bmr: number
    if (gender === 'male') {
      bmr = 10 * weightKg + 6.25 * heightCmFinal - 5 * age.value + 5
    } else {
      bmr = 10 * weightKg + 6.25 * heightCmFinal - 5 * age.value - 161
    }

    const tdee = bmr * activityMultipliers[activityLevel]

    let calories: number
    switch (goal) {
      case 'lose':
        calories = tdee - 500
        break
      case 'gain':
        calories = tdee + 300
        break
      default:
        calories = tdee
    }

    calories = Math.max(calories, 1200)

    const split = macroSplits[dietType]
    const protein = Math.round((calories * split.protein) / 4)
    const carbs = Math.round((calories * split.carbs) / 4)
    const fat = Math.round((calories * split.fat) / 9)

    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      calories: Math.round(calories),
      protein,
      carbs,
      fat,
    })
  }

  const ToggleButton: React.FC<{
    active: boolean
    onClick: () => void
    children: React.ReactNode
    className?: string
  }> = ({ active, onClick, children, className = '' }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
        active
          ? 'bg-gradient-to-r from-brand to-brand-light text-white shadow-md shadow-brand/25'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      } ${className}`}
    >
      {children}
    </button>
  )

  const numberInputClass = 'text-center border border-gray-200 rounded-xl py-2 text-ink font-medium focus:outline-none focus:ring-2 focus:ring-brand/40'
  const stepperClass = 'w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold transition-colors flex items-center justify-center'

  return (
    <div className="min-h-screen">
      <Seo
        title="Free Macro Calculator"
        description="Calculate your daily calories, protein, carbs, and fat in seconds. Free macro calculator by certified nutrition coach Cassie — no sign-up required."
        image="/images/macro-ingredients.webp"
      />

      {/* Hero with imagery */}
      <section className="relative overflow-hidden bg-ink text-white">
        <img
          src="/images/macro-ingredients.webp"
          alt="Macro-friendly whole food ingredients laid out with a food scale"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink/95" />
        <div className="container-max relative px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="eyebrow-blush justify-center mb-4 sm:mb-5">Free Nutrition Tool</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold mb-4 sm:mb-5 leading-tight">
              Macro <em className="italic text-gold-light">Calculator</em>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Get an estimate of your daily calorie and macronutrient needs based
              on the information you provide.
            </p>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed mt-4">
              Your numbers are a starting point, not a set of rules. Use them to
              better understand your nutrition, then adjust your approach to fit
              your goals, preferences, and real life.
            </p>
            <p className="text-sm sm:text-base text-gold-light max-w-2xl mx-auto leading-relaxed mt-5 font-medium">
              Instant estimates. No sign-up required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="section-padding bg-cream">
        <div className="container-max max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-soft border border-gray-100 p-5 sm:p-10 -mt-14 sm:-mt-24 relative z-10"
          >
            <div className="space-y-8">
              {/* Sex used for calculation */}
              <div>
                <label className="form-label mb-1.5">Sex Used for Calculation</label>
                <p className="text-xs text-gray-500 mb-3">
                  The calorie formula below uses sex-based coefficients, so this
                  selection affects the math — not a reflection of anything else.
                </p>
                <div className="flex gap-3">
                  <ToggleButton active={gender === 'female'} onClick={() => setGender('female')} className="flex-1">
                    Female
                  </ToggleButton>
                  <ToggleButton active={gender === 'male'} onClick={() => setGender('male')} className="flex-1">
                    Male
                  </ToggleButton>
                </div>
              </div>

              {/* Age */}
              <div>
                <label className="form-label mb-3">Age</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={age.dec} className={stepperClass}>−</button>
                  <input
                    type="number"
                    value={age.raw}
                    onChange={age.onChange}
                    onBlur={age.onBlur}
                    className={`w-20 ${numberInputClass}`}
                  />
                  <button type="button" onClick={age.inc} className={stepperClass}>+</button>
                </div>
              </div>

              {/* Units */}
              <div>
                <label className="form-label mb-3">Units</label>
                <div className="flex gap-3">
                  <ToggleButton active={units === 'imperial'} onClick={() => setUnits('imperial')} className="flex-1">
                    ft / lbs
                  </ToggleButton>
                  <ToggleButton active={units === 'metric'} onClick={() => setUnits('metric')} className="flex-1">
                    cm / kg
                  </ToggleButton>
                </div>
              </div>

              {/* Height */}
              <div>
                <label className="form-label mb-3">Height</label>
                {units === 'imperial' ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={heightFt.raw}
                        onChange={heightFt.onChange}
                        onBlur={heightFt.onBlur}
                        className={`w-16 ${numberInputClass}`}
                      />
                      <span className="text-gray-500 text-sm">ft</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={heightIn.raw}
                        onChange={heightIn.onChange}
                        onBlur={heightIn.onBlur}
                        className={`w-16 ${numberInputClass}`}
                      />
                      <span className="text-gray-500 text-sm">in</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={heightCm.raw}
                      onChange={heightCm.onChange}
                      onBlur={heightCm.onBlur}
                      className={`w-24 ${numberInputClass}`}
                    />
                    <span className="text-gray-500 text-sm">cm</span>
                  </div>
                )}
              </div>

              {/* Current Weight */}
              <div>
                <label className="form-label mb-3">
                  Current Weight ({units === 'imperial' ? 'lbs' : 'kg'})
                </label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={weight.dec} className={stepperClass}>−</button>
                  <input
                    type="number"
                    value={weight.raw}
                    onChange={weight.onChange}
                    onBlur={weight.onBlur}
                    className={`w-24 ${numberInputClass}`}
                  />
                  <button type="button" onClick={weight.inc} className={stepperClass}>+</button>
                </div>
              </div>

              {/* Goal Weight (optional) */}
              <div>
                <label className="form-label mb-1.5">
                  Goal Weight ({units === 'imperial' ? 'lbs' : 'kg'})
                  <span className="text-gray-400 font-normal normal-case tracking-normal ml-1.5">
                    Optional
                  </span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Not every goal is about a number on the scale — skip this if it
                  doesn't apply to you. It doesn't change your results below.
                </p>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={goalWeight.dec} className={stepperClass}>−</button>
                  <input
                    type="number"
                    value={goalWeight.raw}
                    onChange={goalWeight.onChange}
                    onBlur={goalWeight.onBlur}
                    placeholder="Optional"
                    className={`w-24 ${numberInputClass}`}
                  />
                  <button type="button" onClick={goalWeight.inc} className={stepperClass}>+</button>
                </div>
              </div>

              {/* Goal */}
              <div>
                <label className="form-label mb-3">Goal</label>
                <div className="flex gap-3">
                  {goalOptions.map((opt) => (
                    <ToggleButton
                      key={opt.value}
                      active={goal === opt.value}
                      onClick={() => setGoal(opt.value)}
                      className="flex-1"
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </div>
              </div>

              {/* Activity Level */}
              <div>
                <label className="form-label mb-1.5">Activity Level</label>
                <p className="text-xs text-gray-500 mb-3">
                  Choose the activity level that best reflects your typical week,
                  not your busiest week.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {activityOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setActivityLevel(opt.value)}
                      className={`text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                        activityLevel === opt.value
                          ? 'border-brand bg-brand-soft'
                          : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                      }`}
                    >
                      <div className={`text-sm font-medium ${activityLevel === opt.value ? 'text-brand-dark' : 'text-gray-700'}`}>
                        {opt.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Macro Distribution Preference */}
              <div>
                <label className="form-label mb-1.5">Macro Distribution Preference</label>
                <p className="text-xs text-gray-500 mb-3">
                  These are general starting points, not personalized nutrition
                  prescriptions. Your needs may vary based on your goals, training,
                  and health.
                </p>
                <div className="flex gap-3">
                  {dietOptions.map((opt) => (
                    <ToggleButton
                      key={opt.value}
                      active={dietType === opt.value}
                      onClick={() => setDietType(opt.value)}
                      className="flex-1"
                    >
                      <span className="block">{opt.label}</span>
                      <span className="block text-[10px] opacity-70 mt-0.5">{opt.split}</span>
                    </ToggleButton>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculate}
                className="w-full btn-primary !py-4 text-lg"
              >
                Calculate My Macros
              </button>
            </div>
          </motion.div>

          {/* Results */}
          <AnimatePresence>
            {results && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ duration: 0.5, type: 'spring', damping: 20 }}
                className="mt-8"
              >
                <div className="bg-white rounded-3xl shadow-soft border border-gray-100 p-8 sm:p-10">
                  <h3 className="font-display text-2xl font-semibold text-ink text-center mb-2">Your Daily Targets</h3>
                  <p className="text-center text-gray-500 text-sm mb-8">
                    Based on the Mifflin-St Jeor equation
                  </p>

                  {/* Calorie Target */}
                  <div className="text-center mb-8">
                    <div className="font-display text-6xl font-semibold text-brand mb-1">
                      {results.calories.toLocaleString()}
                    </div>
                    <div className="text-gray-500 text-sm uppercase tracking-widest-xl">
                      Calories per day
                    </div>
                  </div>

                  {/* Macros */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-brand-soft border border-brand/15 rounded-2xl p-4 text-center">
                      <div className="text-3xl font-bold text-brand-dark">{results.protein}g</div>
                      <div className="text-xs text-brand uppercase tracking-wide mt-1">Protein</div>
                      <div className="text-xs text-gray-400 mt-1">{results.protein * 4} cal</div>
                    </div>
                    <div className="bg-gold-soft border border-gold/20 rounded-2xl p-4 text-center">
                      <div className="text-3xl font-bold text-gold">{results.carbs}g</div>
                      <div className="text-xs text-gold uppercase tracking-wide mt-1">Carbs</div>
                      <div className="text-xs text-gray-400 mt-1">{results.carbs * 4} cal</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center">
                      <div className="text-3xl font-bold text-gray-700">{results.fat}g</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Fat</div>
                      <div className="text-xs text-gray-400 mt-1">{results.fat * 9} cal</div>
                    </div>
                  </div>

                  {/* BMR & TDEE */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3 text-center">
                      <div className="text-lg font-semibold text-ink">{results.bmr.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">BMR (Basal Metabolic Rate)</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-3 text-center">
                      <div className="text-lg font-semibold text-ink">{results.tdee.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">TDEE (Maintenance Calories)</div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="flex items-start gap-2 bg-cream rounded-2xl p-4">
                    <Info size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-500 leading-relaxed">
                      These estimates are a starting point. Individual needs vary based on
                      metabolism, body composition, and other factors. For a fully personalized
                      plan, work with a certified coach. If you're pregnant, nursing, or
                      managing a diagnosed medical condition, check with your doctor before
                      using these numbers to guide your nutrition.
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-8 text-center max-w-xl mx-auto"
                >
                  <h4 className="font-display text-xl sm:text-2xl font-semibold text-ink mb-3">
                    Your numbers are a starting point. Your plan should fit your life.
                  </h4>
                  <div className="w-16 h-0.5 bg-brand-blush rounded-full mx-auto mb-5" aria-hidden />
                  <p className="text-gray-600 leading-relaxed">
                    Calories and macros are helpful starting points, but lasting
                    progress takes more than numbers on a screen.
                  </p>
                  <p className="text-gray-600 leading-relaxed mt-3">
                    Whether your goal is to lose body fat, build muscle, maintain
                    your progress, or simply feel stronger, nutrition and purposeful
                    training work together to support your results.
                  </p>
                  <p className="text-ink font-medium mt-3 mb-6">
                    Some women need help with nutrition. Others want a structured
                    workout plan, coaching, and accountability. And some need both.
                    We'll find the right starting point for you.
                  </p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
                    <Link
                      to="/meal-plan"
                      className="inline-flex items-center justify-center text-center rounded-full bg-brand text-white font-semibold px-6 py-3.5 hover:bg-brand-dark transition-colors duration-300 whitespace-normal"
                    >
                      Explore Nutrition Blueprint
                    </Link>
                    <Link
                      to="/coaching"
                      className="inline-flex items-center justify-center text-center rounded-full bg-brand-navy text-white font-semibold px-6 py-3.5 hover:bg-brand-navy/90 transition-colors duration-300 whitespace-normal"
                    >
                      Explore Training & Coaching
                    </Link>
                    <Link
                      to="/book-consultation"
                      className="inline-flex items-center justify-center text-center rounded-full bg-brand-blush text-brand-navy font-semibold px-6 py-3.5 hover:bg-brand-blush/90 transition-colors duration-300 whitespace-normal"
                    >
                      Schedule Your Complimentary Consultation
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  )
}

export default MacroCalculator
